class SwiperComponent extends HTMLElement {
  constructor() {
    super();
    this.swiperElement = this;

    this.enableScrollbar = this.hasAttribute('data-enable-scrollbar') || false;
    this.enablePagination = this.hasAttribute('data-enable-pagination') || false;
    this.customOptionsData = this.getAttribute('data-options');
    this.paginationType = this.getAttribute('data-pagination-type') || 'bullets';

    const clickablePagination = this.paginationType === 'bullets';

    this.customOptions = null;

    this.baseOptions = {
      slidesPerView: 2,
      spaceBetween: 16,
      resistanceRatio: 0.68,
      simulateTouch: true,
      allowTouchMove: true,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next') || this.querySelector('.swiper-btn-next'),
        prevEl: this.querySelector('.swiper-button-prev') || this.querySelector('.swiper-btn-prev'),
      },
      ...(this.enablePagination
        ? {
            pagination: {
              el: this.querySelector('.swiper-pagination'),
              clickable: clickablePagination,
              type: this.paginationType,
            },
          }
        : {}),
      ...(this.enableScrollbar
        ? {
            scrollbar: {
              el: this.querySelector('.swiper-scrollbar'),
              draggable: true,
            },
          }
        : {}),
    };
  }

  get options() {
    if (this._cachedOptions) return this._cachedOptions;

    this._cachedOptions = { ...this.baseOptions };
    if (this.customOptionsData) {
      try {
        const parsed = JSON.parse(this.customOptionsData);
        this._cachedOptions = { ...this._cachedOptions, ...parsed };
      } catch (error) {
        console.error('Error parsing custom options:', error);
      }
    }

    return this._cachedOptions;
  }

  connectedCallback() {
    this.initSwiper();
  }

  initSwiper() {
    // Resolve element references now that children are in the DOM
    if (this.baseOptions.navigation) {
      this.baseOptions.navigation.nextEl = this.querySelector('.swiper-button-next') || this.querySelector('.swiper-btn-next');
      this.baseOptions.navigation.prevEl = this.querySelector('.swiper-button-prev') || this.querySelector('.swiper-btn-prev');
    }
    if (this.baseOptions.pagination) {
      this.baseOptions.pagination.el = this.querySelector('.swiper-pagination');
    }
    if (this.baseOptions.scrollbar) {
      this.baseOptions.scrollbar.el = this.querySelector('.swiper-scrollbar');
    }
    this._cachedOptions = null;

    this.swiper = new Swiper(this.swiperElement, this.options);
    this.updateLock();
    this.swiper.on('resize', () => this.updateLock());
  }

  updateLock() {
    if (!this.swiper) return;
    const { slidesSizesGrid = [], size, params } = this.swiper;
    const gaps = Math.max(slidesSizesGrid.length - 1, 0) * (parseFloat(params.spaceBetween) || 0);
    const slidesWidth = slidesSizesGrid.reduce((total, width) => total + width, 0) + gaps;
    const contentWidth = size - (params.slidesOffsetBefore || 0) - (params.slidesOffsetAfter || 0);
    const locked = slidesWidth <= contentWidth + 1;

    this.classList.toggle('swiper--locked', locked);
    this.swiper.allowTouchMove = !locked;
    if (locked && this.swiper.activeIndex !== 0) this.swiper.slideTo(0, 0);
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
    }
  }
}

customElements.define('swiper-component', SwiperComponent);

class BreakpointSwiper extends SwiperComponent {
  constructor() {
    super();

    this.onlyFor = this.getAttribute('data-only-for') || 'mobile';
    this.mediaToWatch = this.getAttribute('data-media-to-watch') || 990;

    this.mql = window.matchMedia(`(min-width: ${this.mediaToWatch}px)`);
    this.mql.addEventListener('change', this.onMediaChange.bind(this));
  }

  connectedCallback() {
    const mediaMatches = this.mql.matches;
    const shouldInit = this.onlyFor === 'desktop' ? mediaMatches : !mediaMatches;

    if (!shouldInit) return;

    super.connectedCallback();
  }

  onMediaChange() {
    const mediaMatches = this.mql.matches; // true = desktop, false = mobile

    // Determine if swiper should be active based on device type and breakpoint
    const shouldBeActive = this.onlyFor === 'desktop' ? mediaMatches : !mediaMatches;

    if (shouldBeActive && !this.swiper) {
      this.initSwiper();
    } else if (!shouldBeActive && this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }
  }
}

customElements.define('breakpoint-swiper', BreakpointSwiper);
